import { MagnifyingGlassIcon } from "@radix-ui/react-icons";
import { XIcon } from "lucide-react";
import {
  Button,
  Input,
  SearchField,
  SearchFieldProps,
} from "react-aria-components";

import { cn } from "@/lib/utils";

interface SearchInputProps extends Omit<SearchFieldProps, "ref"> {
  "aria-label": string;
  ref?: React.Ref<HTMLInputElement>;
  placeholder?: string;
  prefix?: React.ReactNode;
  sizeVariant?: "sm" | "md" | "lg";
  wrapperRef?: React.Ref<HTMLDivElement>;
  wrapperAsDiv?: boolean;
  icon?: React.ReactNode;
}

export function SearchInput({
  className,
  placeholder = "Search",
  prefix,
  sizeVariant = "md",
  ref,
  wrapperRef,
  wrapperAsDiv = false,
  icon,
  ...props
}: SearchInputProps) {
  const Component = wrapperAsDiv ? "div" : SearchField;
  return (
    <Component
      ref={wrapperRef}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      {...(props as any)}
      className={cn(
        "group",
        "font-medium",
        "flex items-center",
        "bg-slate-ghost",
        "border border-slate-normal",
        "focus-within:outline-hidden focus-within:ring-2 focus-within:ring-offset-2",
        "w-full placeholder:text-slate-dim disabled:cursor-not-allowed disabled:opacity-50 rounded-lg",
        sizeVariant === "sm" && "h-8",
        sizeVariant === "md" && "h-10",
        sizeVariant === "lg" && "h-12",
        className,
      )}
    >
      <div
        className={cn(
          "flex items-center justify-center w-10 h-10",
          sizeVariant === "sm" && "w-8 h-8",
          sizeVariant === "md" && "w-10 h-10",
          sizeVariant === "lg" && "w-12 h-12",
        )}
      >
        {icon || (
          <MagnifyingGlassIcon
            className={cn("h-4 w-4", sizeVariant === "lg" && "h-5 w-5")}
          />
        )}
      </div>
      {prefix && (
        <div className="flex items-center justify-center">{prefix}</div>
      )}
      <Input
        ref={ref}
        placeholder={placeholder}
        className={cn(
          "bg-transparent",
          "text-slate-normal placeholder:text-slate-dim",
          "w-full ",
          "focus:outline-hidden",
          sizeVariant === "sm" && "py-1 text-xs",
          sizeVariant === "md" && "py-2",
        )}
      />
      <Button
        className={cn(
          "mr-1",
          "group-data-empty:hidden",
          "flex items-center justify-center p-2 h-8 w-8 rounded-md",
          "bg-slatea-ghost",
        )}
      >
        <span className="sr-only">Clear search</span>
        <XIcon />
      </Button>
    </Component>
  );
}
